import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Send,
  MessageSquare,
  Lightbulb,
  Trash2,
  RefreshCw,
  AlertCircle,
  Sparkles,
} from 'lucide-react'
import * as api from '../services/api'
import type { ChatMessage } from '../types'

interface DisplayMessage extends ChatMessage {
  isNew?: boolean
}

function SuggestedQuestion({ question, onClick }: { question: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-2 p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg text-left hover:bg-slate-700/30 hover:border-slate-600/50 transition-all group"
    >
      <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
      <span className="text-sm text-slate-300 group-hover:text-slate-200">{question}</span>
    </button>
  )
}

export default function Chat() {
  const { projectId } = useParams<{ projectId: string }>()
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  const fetchHistory = useCallback(async () => {
    if (!projectId) return
    setLoadingHistory(true)
    try {
      const [historyRes, suggestionsRes] = await Promise.all([
        api.getChatHistory(projectId),
        api.getChatSuggestions(projectId, 4),
      ])
      setMessages(historyRes.data.messages)
      setSuggestions(suggestionsRes.data.suggestions)
    } catch (err) {
      console.error('Failed to fetch chat history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim()
    if (!text || !projectId || loading) return

    setInput('')
    setError(null)

    // Add user message immediately
    const userMessage: DisplayMessage = {
      id: `temp-${Date.now()}`,
      project_id: projectId,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      isNew: true,
    }
    setMessages((prev) => [...prev, userMessage])

    setLoading(true)
    try {
      const response = await api.sendChatMessage(projectId, {
        message: text,
      })

      // Add assistant response
      const assistantMessage: DisplayMessage = {
        id: response.data.id,
        project_id: projectId,
        role: 'assistant',
        content: response.data.content,
        sources: response.data.sources,
        created_at: new Date().toISOString(),
        isNew: true,
      }
      setMessages((prev) => [...prev, assistantMessage])

      // Refresh suggestions
      const suggestionsRes = await api.getChatSuggestions(projectId, 4)
      setSuggestions(suggestionsRes.data.suggestions)
    } catch (err) {
      console.error('Failed to send message:', err)
      setError('Failed to send message. Please try again.')
      // Remove the optimistic user message on error
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id))
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  const handleClearHistory = async () => {
    if (!projectId) return
    if (!confirm('Are you sure you want to clear the chat history?')) return

    try {
      await api.clearChatHistory(projectId)
      setMessages([])
      await fetchHistory()
    } catch (err) {
      console.error('Failed to clear history:', err)
      setError('Failed to clear chat history')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Project Chat</h1>
          <p className="text-slate-400 mt-1">Ask questions about your project documents</p>
        </div>
        {messages.length > 0 && (
          <button onClick={handleClearHistory} className="btn-ghost btn-sm text-red-400">
            <Trash2 className="w-4 h-4" />
            Clear History
          </button>
        )}
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex flex-col glass-panel overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-300 mb-2">Start a conversation</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Ask questions about your WBS, SOW, meeting transcripts, and other project documents.
                The AI will provide answers with source citations.
              </p>
              {suggestions.length > 0 && (
                <div className="space-y-2 max-w-lg mx-auto">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">
                    Suggested questions
                  </p>
                  {suggestions.map((question, index) => (
                    <SuggestedQuestion
                      key={index}
                      question={question}
                      onClick={() => handleSend(question)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${message.isNew ? 'animate-slideUp' : ''}`}>
                    {message.role === 'user' ? (
                      <div className="chat-message-user">
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    ) : (
                      <div className="chat-message-assistant">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-cyan-400" />
                          <span className="text-xs text-slate-500">AI Response</span>
                        </div>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="chat-message-assistant animate-pulse">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" />
                      <span className="text-slate-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Suggestions (when there are messages) */}
        {messages.length > 0 && suggestions.length > 0 && !loading && (
          <div className="px-4 py-2 border-t border-slate-700/50">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              <Lightbulb className="w-3 h-3 text-amber-500 flex-shrink-0" />
              {suggestions.slice(0, 3).map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSend(question)}
                  className="text-xs text-slate-400 hover:text-slate-200 bg-slate-800/50 px-2 py-1 rounded-full whitespace-nowrap hover:bg-slate-700/50 transition-colors"
                >
                  {question.length > 50 ? `${question.slice(0, 50)}...` : question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 py-2 border-t border-slate-700/50">
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-slate-700/50">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about your project..."
              className="textarea flex-1 min-h-[44px] max-h-32"
              rows={1}
              disabled={loading}
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="btn-primary"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  )
}
